/**
 * Comisiones de los profesionales. La administración configura las reglas (porcentaje o valor fijo por profesional y tipo de tratamiento),
 * liquida cada quincena sobre lo cobrado (descontando laboratorio y materiales), aprueba y registra el pago. Cada profesional ve las suyas.
 */
import { useEffect, useState } from "react";
import { api, apiBlob, puede, ErrorApi } from "../api.ts";
import { dinero, leerMonto, quincenaDe, quincenaAnterior, quincenaSiguiente, TIPOS_TRATAMIENTO, METODOS_PAGO, type Quincena } from "../../../dominio/src/index.ts";
import { AvisoError, hoy, Seccion, useAccion } from "./comun.tsx";
import { FechaInput } from "../componentes/CampoFecha.tsx";

interface Profesional { id: string; nombre: string; rol: string; activo: boolean; reglas: number; forma: "quincena" | "procedimiento" }
interface Regla { ambito: "general" | "tipo" | "codigo"; clave: string; modo: "porcentaje" | "fijo"; porcentaje: number; fijo: number; cuando: "cobrado" | "realizado" }
interface Linea {
  itemId: string; paciente: string; codigo: string; descripcion: string; diente: number | null; realizadoEn: string; valorNeto: number; costoLab: number; costoMat: number;
  fraccionCobrada: number; textoRegla: string; regla: Regla | null; base: number; acumulado: number; yaLiquidado: number; aLiquidar: number;
}
interface Calculo { profesional: { id: string; nombre: string }; periodo: Quincena; lineas: Linea[]; total: number; nuevas: number; sinRegla: number }
interface LineaLiq { itemId: string; paciente: string; codigo: string; descripcion: string; diente: number | null; realizadoEn: string; valorNeto: number; costos: number; fraccionCobrada: number; regla: string; base: number; acumulado: number; monto: number }
interface Liquidacion {
  id: string; numero: number; usuario_id: string; profesional: string; nombre_periodo: string; desde: string; hasta: string; total: number; estado: "aprobada" | "pagada" | "anulada";
  aprobada_en: string; aprobada_por: string; pagada_en: string | null; metodo_pago: string; nota_pago: string; motivo_anulacion: string; lineas: LineaLiq[];
}
interface Resumen { id: string; numero: number; tipo?: "quincena" | "procedimientos"; profesional?: string; usuario_id?: string; nombre_periodo: string; total: number; estado: "aprobada" | "pagada" | "anulada"; pagada_en: string | null; lineas?: number }

const fechaCorta = (iso: string) => new Date(iso + "T12:00:00").toLocaleDateString("es-EC", { day: "2-digit", month: "short" });
const fechaLarga = (iso: string) => new Date(iso + "T12:00:00").toLocaleDateString("es-EC", { day: "numeric", month: "short", year: "numeric" });
const dolares = (c: number) => (c / 100).toFixed(2);
const CHIP: Record<string, string> = { aprobada: "rojo", pagada: "azul", anulada: "" };
const NOMBRE: Record<string, string> = { aprobada: "Aprobada · por pagar", pagada: "Pagada", anulada: "Anulada" };
const pctCobrado = (f: number) => (f >= 0.9995 ? "100 %" : `${Math.round(f * 100)} %`);

export function Comisiones() {
  return puede("comisiones:admin") ? <Administracion /> : <MisComisiones />;
}

/* ---------------- administración ---------------- */

function Administracion() {
  const [vista, setVista] = useState<"liquidar" | "liquidaciones" | "reglas" | "costos">("liquidar");
  const [abrir, setAbrir] = useState<string | null>(null);
  return (
    <Seccion titulo="Comisiones" acciones={
      <span className="chipset" role="tablist">
        <button className="tog" aria-pressed={vista === "liquidar"} onClick={() => setVista("liquidar")}>Liquidar quincena</button>
        <button className="tog" aria-pressed={vista === "liquidaciones"} onClick={() => setVista("liquidaciones")}>Liquidaciones</button>
        <button className="tog" aria-pressed={vista === "reglas"} onClick={() => setVista("reglas")}>Reglas</button>
        <button className="tog" aria-pressed={vista === "costos"} onClick={() => setVista("costos")}>Costos habituales</button>
      </span>}>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>Se calcula sobre lo <b>cobrado</b>, descontando laboratorio y materiales; los especialistas externos pueden tener un valor fijo por procedimiento. La liquidación se hace cada quincena y la apruebas tú.</p>
      {vista === "liquidar" && <Liquidar alAprobar={(id) => { setAbrir(id); setVista("liquidaciones"); }} />}
      {vista === "liquidaciones" && <Liquidaciones abrirInicial={abrir} />}
      {vista === "reglas" && <Reglas />}
      {vista === "costos" && <CostosHabituales />}
    </Seccion>
  );
}

/* ---- liquidar una quincena ---- */

function Liquidar({ alAprobar }: { alAprobar: (id: string) => void }) {
  const [profs, setProfs] = useState<Profesional[]>([]);
  const [uid, setUid] = useState("");
  const [q, setQ] = useState<Quincena>(() => quincenaAnterior(quincenaDe(hoy())));
  // «por procedimiento»: se paga lo ya terminado sin esperar a la quincena
  const [modo, setModo] = useState<"quincena" | "procedimiento">("quincena");
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [calc, setCalc] = useState<Calculo | null>(null);
  const [costos, setCostos] = useState<Record<string, { lab: string; mat: string }>>({});
  const a = useAccion();
  useEffect(() => {
    api<{ profesionales: Profesional[] }>("GET", "/api/comisiones/profesionales").then((r) => {
      setProfs(r.profesionales);
      const primero = r.profesionales.find((p) => p.activo);
      setUid((x) => x || primero?.id || "");
      if (primero) setModo(primero.forma === "procedimiento" ? "procedimiento" : "quincena");
    }).catch(a.setError);
  }, []);
  const elegirProfesional = (id: string) => { setUid(id); const p = profs.find((x) => x.id === id); if (p) setModo(p.forma === "procedimiento" ? "procedimiento" : "quincena"); };
  const cargar = () => {
    if (!uid) return Promise.resolve();
    return api<Calculo>("GET", `/api/comisiones/calculo?usuario=${uid}&hasta=${modo === "quincena" ? q.hasta : hoy()}`).then((r) => {
      setCalc(r);
      setCostos(Object.fromEntries(r.lineas.map((l) => [l.itemId, { lab: l.costoLab ? dolares(l.costoLab) : "", mat: l.costoMat ? dolares(l.costoMat) : "" }])));
    }).catch(a.setError);
  };
  useEffect(() => { setCalc(null); setSel(new Set()); cargar(); }, [uid, q.hasta, modo]);
  const terminada = modo === "procedimiento" || q.hasta <= hoy();
  async function guardarCostos(l: Linea) {
    const c = costos[l.itemId] ?? { lab: "", mat: "" };
    const lab = c.lab.trim() === "" ? 0 : leerMonto(c.lab), mat = c.mat.trim() === "" ? 0 : leerMonto(c.mat);
    if (lab === null || mat === null) { a.setError(new ErrorApi(400, "Escribe los costos como 12.50")); return; }
    if (lab === l.costoLab && mat === l.costoMat) return;
    await a.correr(() => api("PUT", `/api/comisiones/costos/${l.itemId}`, { laboratorio: lab / 100, materiales: mat / 100 }));
    await cargar();
  }
  const nuevas = calc?.lineas.filter((l) => l.aLiquidar !== 0) ?? [];
  const resto = calc?.lineas.filter((l) => l.aLiquidar === 0) ?? [];
  const porProc = modo === "procedimiento";
  const alternar = (id: string) => setSel((x) => { const n = new Set(x); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const elegidas = nuevas.filter((l) => sel.has(l.itemId));
  const totalElegido = elegidas.reduce((s, l) => s + l.aLiquidar, 0);
  const fila = (l: Linea) => {
    const c = costos[l.itemId] ?? { lab: "", mat: "" };
    return (
      <tr key={l.itemId}>
        {porProc && l.aLiquidar !== 0 && <td><input type="checkbox" checked={sel.has(l.itemId)} onChange={() => alternar(l.itemId)} aria-label={`Pagar ${l.descripcion} de ${l.paciente}`} /></td>}
        {porProc && l.aLiquidar === 0 && <td />}
        <td className="mono">{fechaCorta(l.realizadoEn)}</td>
        <td>{l.paciente}</td>
        <td>{l.descripcion}{l.diente ? <span className="muted"> · pieza {l.diente}</span> : ""}</td>
        <td className="mono num">{dinero(l.valorNeto)}</td>
        <td><input className="entrada mono" style={{ width: 70 }} inputMode="decimal" value={c.lab} placeholder="0.00" onChange={(e) => setCostos({ ...costos, [l.itemId]: { ...c, lab: e.target.value } })} onBlur={() => guardarCostos(l)} aria-label={`Laboratorio de ${l.descripcion} de ${l.paciente}`} /></td>
        <td><input className="entrada mono" style={{ width: 70 }} inputMode="decimal" value={c.mat} placeholder="0.00" onChange={(e) => setCostos({ ...costos, [l.itemId]: { ...c, mat: e.target.value } })} onBlur={() => guardarCostos(l)} aria-label={`Materiales de ${l.descripcion} de ${l.paciente}`} /></td>
        <td className="mono">{pctCobrado(l.fraccionCobrada)}</td>
        <td className="mono">{l.textoRegla}</td>
        <td className="mono num"><b style={l.aLiquidar < 0 ? { color: "var(--red)" } : undefined}>{dinero(l.aLiquidar)}</b>{l.yaLiquidado !== 0 && <div className="muted" style={{ fontSize: 11 }}>ya liquidado {dinero(l.yaLiquidado)}</div>}</td>
      </tr>
    );
  };
  const cabecera = <thead><tr>{porProc && <th />}<th>Realizado</th><th>Paciente</th><th>Procedimiento</th><th>Valor</th><th>Laboratorio</th><th>Materiales</th><th>Cobrado</th><th>Regla</th><th>A liquidar</th></tr></thead>;
  return (
    <>
      <div className="fila" style={{ alignItems: "flex-end", flexWrap: "wrap" }}>
        <label className="campo" style={{ flex: "1 1 220px" }}>Profesional
          <select value={uid} onChange={(e) => elegirProfesional(e.target.value)}>{profs.map((p) => <option key={p.id} value={p.id}>{p.nombre}{p.activo ? "" : " (inactivo)"}{p.forma === "procedimiento" ? " · por procedimiento" : ""}</option>)}</select>
        </label>
        <span className="chipset" role="tablist" aria-label="Cómo liquidar">
          <button className="tog" aria-pressed={modo === "quincena"} onClick={() => setModo("quincena")}>Por quincena</button>
          <button className="tog" aria-pressed={modo === "procedimiento"} onClick={() => setModo("procedimiento")}>Por procedimiento</button>
        </span>
        {modo === "quincena" && (
          <div className="fila" style={{ gap: 6 }}>
            <button className="btn chico" onClick={() => setQ(quincenaAnterior(q))} aria-label="Quincena anterior">‹</button>
            <b style={{ minWidth: 190, textAlign: "center" }}>{q.nombre}</b>
            <button className="btn chico" onClick={() => setQ(quincenaSiguiente(q))} aria-label="Quincena siguiente">›</button>
          </div>
        )}
      </div>
      {porProc && <p className="muted" style={{ margin: 0, fontSize: 13 }}>Para quien cobra por procedimiento terminado: marca los que se pagan ahora, sin esperar a la quincena. Se paga lo que le corresponde hoy según lo cobrado (o el valor fijo, si su regla es «al realizar»); lo que falte por cobrar se liquidará después.</p>}
      <AvisoError error={a.error} />
      {!calc ? <p className="muted">Calculando…</p> : (
        <>
          {calc.sinRegla > 0 && <div className="aviso info">{calc.sinRegla === 1 ? "1 procedimiento no tiene regla de comisión" : `${calc.sinRegla} procedimientos no tienen regla de comisión`}: defínela en la pestaña <b>Reglas</b>.</div>}
          {!terminada && <div className="aviso info">Esa quincena todavía no termina: podrás aprobarla cuando termine.</div>}
          {nuevas.length === 0 ? <p className="muted">{porProc ? "No hay procedimientos terminados con comisión pendiente." : "No hay comisiones nuevas para liquidar en esta quincena."}</p> : (
            <div className="tabla-scroll"><table className="tabla">{cabecera}<tbody>{nuevas.map(fila)}</tbody>
              <tfoot><tr><td colSpan={porProc ? 9 : 8}><b>{porProc ? `Total de los seleccionados (${elegidas.length})` : "Total a liquidar"}</b></td><td className="mono num"><b>{dinero(porProc ? totalElegido : calc.total)}</b></td></tr></tfoot></table></div>
          )}
          {porProc && nuevas.length > 0 && <div className="fila"><button className="btn chico" onClick={() => setSel(new Set(nuevas.map((l) => l.itemId)))}>Marcar todos</button><button className="btn chico" onClick={() => setSel(new Set())}>Quitar marcas</button></div>}
          <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>
            Escribe el costo de laboratorio y de materiales de cada procedimiento: se descuenta del valor antes de calcular el porcentaje. Los abonos cubren primero los procedimientos más antiguos del paciente; si se anula un abono, el ajuste entra en la siguiente liquidación.
          </p>
          <div className="fila">
            <button className="btn pri" disabled={a.ocupado || !terminada || nuevas.length === 0 || (porProc && elegidas.length === 0)} onClick={async () => {
              const monto = porProc ? totalElegido : calc.total;
              if (!confirm(`¿Aprobar la liquidación de ${calc.profesional.nombre} por ${dinero(monto)}${porProc ? ` (${elegidas.length} procedimiento${elegidas.length === 1 ? "" : "s"})` : ""}? Después no se puede modificar.`)) return;
              const cuerpo = porProc ? { usuarioId: uid, items: elegidas.map((l) => l.itemId) } : { usuarioId: uid, hasta: q.hasta };
              const r = await a.correr(() => api<Liquidacion>("POST", "/api/comisiones/liquidaciones", cuerpo));
              if (r) alAprobar(r.id);
            }}>Aprobar liquidación{porProc ? (elegidas.length ? ` · ${dinero(totalElegido)}` : "") : nuevas.length ? ` · ${dinero(calc.total)}` : ""}</button>
          </div>
          {resto.length > 0 && (
            <details>
              <summary>Procedimientos realizados sin cambios en esta quincena ({resto.length})</summary>
              <div className="tabla-scroll"><table className="tabla">{cabecera}<tbody>{resto.map(fila)}</tbody></table></div>
              <small className="muted">Los que aún no se cobran no generan comisión todavía (con regla de porcentaje); la generarán cuando el paciente pague.</small>
            </details>
          )}
        </>
      )}
    </>
  );
}

/* ---- liquidaciones aprobadas ---- */

function Liquidaciones({ abrirInicial }: { abrirInicial: string | null }) {
  const [lista, setLista] = useState<Resumen[]>([]);
  const [abierta, setAbierta] = useState<string | null>(abrirInicial);
  const a = useAccion();
  const cargar = () => api<Resumen[]>("GET", "/api/comisiones/liquidaciones").then(setLista).catch(a.setError);
  useEffect(() => { cargar(); }, []);
  return (
    <>
      <AvisoError error={a.error} />
      {lista.length === 0 && <p className="muted">Todavía no hay liquidaciones aprobadas.</p>}
      <ul className="lista-facturas">
        {lista.map((x) => (
          <li key={x.id} className={abierta === x.id ? "abierto" : ""}>
            <button className="fila-factura" onClick={() => setAbierta(abierta === x.id ? null : x.id)} aria-expanded={abierta === x.id}>
              <span className="mono">N.º {String(x.numero).padStart(5, "0")}</span>
              <b className="cliente">{x.profesional}</b>
              <span className="muted">{x.nombre_periodo}</span>
              <span className={`chip ${CHIP[x.estado]}`}>{NOMBRE[x.estado]}{x.estado === "pagada" && x.pagada_en ? ` · ${fechaCorta(x.pagada_en)}` : ""}</span>
              <b className="mono num">{dinero(x.total)}</b>
            </button>
            {abierta === x.id && <DetalleLiquidacion id={x.id} admin alCambiar={cargar} />}
          </li>
        ))}
      </ul>
    </>
  );
}

function DetalleLiquidacion({ id, admin, alCambiar }: { id: string; admin: boolean; alCambiar?: () => void }) {
  const [q, setQ] = useState<Liquidacion | null>(null);
  const [pago, setPago] = useState({ fecha: hoy(), metodo: "Transferencia", nota: "" });
  const [modo, setModo] = useState<"" | "pagar" | "anular">("");
  const [motivo, setMotivo] = useState("");
  const a = useAccion();
  useEffect(() => { api<Liquidacion>("GET", `/api/comisiones/liquidaciones/${id}`).then(setQ).catch(a.setError); }, [id]);
  if (!q) return <div className="detalle-factura"><AvisoError error={a.error} />{!a.error && <p className="muted">Cargando…</p>}</div>;
  const accion = async (ruta: string, cuerpo: unknown) => {
    const r = await a.correr(() => api<Liquidacion>("POST", `/api/comisiones/liquidaciones/${id}/${ruta}`, cuerpo));
    if (r) { setQ(r); setModo(""); alCambiar?.(); }
  };
  return (
    <div className="detalle-factura" style={{ display: "grid", gap: 10 }}>
      <div className="fila" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
        <span className="muted">Aprobada por {q.aprobada_por} el {fechaLarga(q.aprobada_en.slice(0, 10))} · {fechaLarga(q.desde)} al {fechaLarga(q.hasta)}{q.estado === "pagada" && q.pagada_en ? ` · pagada el ${fechaLarga(q.pagada_en)} (${q.metodo_pago})` : ""}{q.estado === "anulada" ? ` · anulada: ${q.motivo_anulacion}` : ""}</span>
        <span className="fila">
          {admin && q.total > 0 && q.estado !== "anulada" && puede("caja:anular") && (
            <button className="btn chico" title="Si la clínica es agente de retención y este profesional le factura, abre el comprobante de retención con el valor ya puesto"
              onClick={() => { location.hash = `#/facturas?retencion=1&nombre=${encodeURIComponent(q.profesional)}&base=${q.total}`; }}>Preparar retención</button>
          )}
          <button className="btn chico" onClick={async () => { const url = await apiBlob(`/api/comisiones/liquidaciones/${id}/pdf`).catch(() => null); if (url) window.open(url, "_blank"); }}>Descargar PDF</button>
        </span>
      </div>
      <div className="tabla-scroll"><table className="tabla">
        <thead><tr><th>Realizado</th><th>Paciente</th><th>Procedimiento</th><th>Valor</th><th>Costos</th><th>Cobrado</th><th>Regla</th><th>Comisión</th></tr></thead>
        <tbody>{q.lineas.map((l) => (
          <tr key={l.itemId + l.monto}>
            <td className="mono">{fechaCorta(l.realizadoEn)}</td><td>{l.paciente}</td><td>{l.descripcion}{l.diente ? <span className="muted"> · pieza {l.diente}</span> : ""}</td>
            <td className="mono num">{dinero(l.valorNeto)}</td><td className="mono num">{l.costos ? dinero(l.costos) : "—"}</td><td className="mono">{pctCobrado(l.fraccionCobrada)}</td><td className="mono">{l.regla}</td>
            <td className="mono num"><b style={l.monto < 0 ? { color: "var(--red)" } : undefined}>{dinero(l.monto)}</b></td>
          </tr>))}</tbody>
        <tfoot><tr><td colSpan={7}><b>Total{q.total < 0 ? " (a favor de la clínica)" : ""}</b></td><td className="mono num"><b>{dinero(q.total)}</b></td></tr></tfoot>
      </table></div>
      <AvisoError error={a.error} />
      {admin && q.estado === "aprobada" && (
        modo === "pagar" ? (
          <div className="fila" style={{ alignItems: "flex-end", flexWrap: "wrap" }}>
            <label className="campo">Fecha de pago<FechaInput max={hoy()} value={pago.fecha} onChange={(e) => setPago({ ...pago, fecha: e.target.value })} /></label>
            <label className="campo">Forma de pago<select value={pago.metodo} onChange={(e) => setPago({ ...pago, metodo: e.target.value })}>{[...METODOS_PAGO, "Cheque"].map((m) => <option key={m}>{m}</option>)}</select></label>
            <label className="campo" style={{ flex: "1 1 200px" }}>Nota (opcional)<input value={pago.nota} maxLength={300} onChange={(e) => setPago({ ...pago, nota: e.target.value })} /></label>
            <button className="btn pri" disabled={a.ocupado} onClick={() => accion("pagar", pago)}>Confirmar pago</button>
            <button className="btn" onClick={() => setModo("")}>Cancelar</button>
          </div>
        ) : modo === "anular" ? (
          <div className="fila" style={{ alignItems: "flex-end" }}>
            <label className="campo" style={{ flex: 1 }}>Motivo de la anulación<input value={motivo} maxLength={300} onChange={(e) => setMotivo(e.target.value)} autoFocus /></label>
            <button className="btn peligro" disabled={a.ocupado || !motivo.trim()} onClick={() => accion("anular", { motivo })}>Anular liquidación</button>
            <button className="btn" onClick={() => setModo("")}>Volver</button>
          </div>
        ) : (
          <div className="fila">
            <button className="btn pri chico" onClick={() => setModo("pagar")}>Marcar como pagada</button>
            <button className="btn chico peligro" onClick={() => setModo("anular")}>Anular</button>
          </div>
        )
      )}
    </div>
  );
}

/* ---- reglas ---- */

const REGLA_VACIA: Regla = { ambito: "tipo", clave: TIPOS_TRATAMIENTO[0], modo: "porcentaje", porcentaje: 30, fijo: 0, cuando: "cobrado" };

function Reglas() {
  const [profs, setProfs] = useState<Profesional[]>([]);
  const [uid, setUid] = useState("");
  const [reglas, setReglas] = useState<Regla[] | null>(null);
  const [forma, setForma] = useState<"quincena" | "procedimiento">("quincena");
  const [valores, setValores] = useState<string[]>([]);
  const [aranceles, setAranceles] = useState<{ codigo: string; nombre: string }[]>([]);
  const [ok, setOk] = useState("");
  const a = useAccion();
  useEffect(() => {
    api<{ profesionales: Profesional[] }>("GET", "/api/comisiones/profesionales").then((r) => { setProfs(r.profesionales); setUid((x) => x || r.profesionales.find((p) => p.activo)?.id || ""); }).catch(a.setError);
    api<{ codigo: string; nombre: string }[]>("GET", "/api/aranceles").then(setAranceles).catch(() => undefined);
  }, []);
  const poner = (r: Regla[]) => { setReglas(r); setValores(r.map((x) => (x.modo === "fijo" ? dolares(x.fijo) : String(x.porcentaje)))); };
  useEffect(() => { if (uid) { setReglas(null); setOk(""); api<{ reglas: Regla[]; forma: "quincena" | "procedimiento" }>("GET", `/api/comisiones/reglas/${uid}`).then((r) => { poner(r.reglas); setForma(r.forma); }).catch(a.setError); } }, [uid]);
  const cambiar = (i: number, x: Partial<Regla>) => { if (reglas) { setReglas(reglas.map((r, j) => (j === i ? { ...r, ...x } : r))); setOk(""); } };
  const cambiarValor = (i: number, v: string) => { setValores(valores.map((x, j) => (j === i ? v : x))); setOk(""); };
  async function guardar() {
    if (!reglas) return;
    const cuerpo = reglas.map((r, i) => ({ ambito: r.ambito, clave: r.ambito === "general" ? "" : r.clave, modo: r.modo, cuando: r.cuando, porcentaje: r.modo === "porcentaje" ? valores[i] : 0, fijo: r.modo === "fijo" ? valores[i] : 0 }));
    const r = await a.correr(() => api<{ reglas: Regla[]; forma: "quincena" | "procedimiento" }>("PUT", `/api/comisiones/reglas/${uid}`, { reglas: cuerpo, forma }));
    if (r) { poner(r.reglas); setForma(r.forma); setProfs(profs.map((p) => (p.id === uid ? { ...p, forma: r.forma, reglas: r.reglas.length } : p))); setOk("Reglas guardadas."); }
  }
  return (
    <>
      <label className="campo" style={{ maxWidth: 320 }}>Profesional
        <select value={uid} onChange={(e) => setUid(e.target.value)}>{profs.map((p) => <option key={p.id} value={p.id}>{p.nombre}{p.activo ? "" : " (inactivo)"}{p.reglas ? ` · ${p.reglas} regla${p.reglas === 1 ? "" : "s"}` : ""}</option>)}</select>
      </label>
      <label className="campo" style={{ maxWidth: 360 }}>Forma de pago
        <select value={forma} onChange={(e) => { setForma(e.target.value as typeof forma); setOk(""); }}>
          <option value="quincena">Cada quincena</option><option value="procedimiento">Por procedimiento terminado</option>
        </select>
        <small className="hint">Solo cambia cómo se abre la pantalla de liquidar; siempre puedes elegir la otra.</small>
      </label>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        Manda la regla más específica: la del <b>procedimiento</b>, si no la de su <b>tipo de tratamiento</b>, si no la <b>general</b>. Un <b>valor fijo</b> sirve para especialistas externos (por ejemplo, $45 por cada extracción); se puede generar al realizar el procedimiento o solo cuando se cobre.
      </p>
      <AvisoError error={a.error} />
      {!reglas ? <p className="muted">Cargando…</p> : (
        <>
          {reglas.length === 0 && <p className="muted">Este profesional todavía no tiene reglas: no genera comisión.</p>}
          <div style={{ display: "grid", gap: 8 }}>
            {reglas.map((r, i) => (
              <div key={i} className="fila" style={{ alignItems: "flex-end", flexWrap: "wrap", gap: 8 }}>
                <label className="campo">Aplica a
                  <select value={r.ambito} onChange={(e) => { const ambito = e.target.value as Regla["ambito"]; cambiar(i, { ambito, clave: ambito === "tipo" ? TIPOS_TRATAMIENTO[0] : ambito === "codigo" ? aranceles[0]?.codigo ?? "" : "" }); }}>
                    <option value="general">Todo lo que realice</option><option value="tipo">Un tipo de tratamiento</option><option value="codigo">Un procedimiento</option>
                  </select>
                </label>
                {r.ambito === "tipo" && <label className="campo">Tipo<select value={r.clave} onChange={(e) => cambiar(i, { clave: e.target.value })}>{TIPOS_TRATAMIENTO.map((t) => <option key={t}>{t}</option>)}</select></label>}
                {r.ambito === "codigo" && <label className="campo">Procedimiento<select value={r.clave} onChange={(e) => cambiar(i, { clave: e.target.value })}>{aranceles.map((x) => <option key={x.codigo} value={x.codigo}>{x.codigo} · {x.nombre}</option>)}</select></label>}
                <label className="campo">Se paga
                  <select value={r.modo} onChange={(e) => { const modo = e.target.value as Regla["modo"]; cambiar(i, { modo }); cambiarValor(i, modo === "fijo" ? "0.00" : "30"); }}>
                    <option value="porcentaje">Un porcentaje de lo cobrado</option><option value="fijo">Un valor fijo por procedimiento</option>
                  </select>
                </label>
                <label className="campo corto">{r.modo === "fijo" ? "Valor ($)" : "Porcentaje (%)"}<input className="mono" inputMode="decimal" value={valores[i] ?? ""} onChange={(e) => cambiarValor(i, e.target.value)} /></label>
                {r.modo === "fijo" && <label className="campo">Se genera<select value={r.cuando} onChange={(e) => cambiar(i, { cuando: e.target.value as Regla["cuando"] })}><option value="realizado">Al realizar el procedimiento</option><option value="cobrado">Cuando se cobre</option></select></label>}
                <button type="button" className="btn chico" onClick={() => { setReglas(reglas.filter((_, j) => j !== i)); setValores(valores.filter((_, j) => j !== i)); setOk(""); }} aria-label="Quitar la regla">✕</button>
              </div>
            ))}
          </div>
          <div className="fila">
            <button className="btn chico" onClick={() => { setReglas([...reglas, { ...REGLA_VACIA, ambito: reglas.some((r) => r.ambito === "general") ? "tipo" : "general", clave: reglas.some((r) => r.ambito === "general") ? TIPOS_TRATAMIENTO[0] : "" }]); setValores([...valores, "30"]); setOk(""); }}>+ Agregar regla</button>
            <button className="btn pri" disabled={a.ocupado} onClick={guardar}>Guardar reglas</button>
            {ok && <span className="muted" style={{ fontSize: 13 }}>{ok}</span>}
          </div>
        </>
      )}
    </>
  );
}

/* ---- costo habitual de laboratorio y materiales por tratamiento ---- */

interface CostoTratamiento { codigo: string; nombre: string; precio: number; activo: boolean; laboratorio: number; materiales: number }

function CostosHabituales() {
  const [lista, setLista] = useState<CostoTratamiento[] | null>(null);
  const [campos, setCampos] = useState<Record<string, { lab: string; mat: string }>>({});
  const [buscar, setBuscar] = useState("");
  const [ok, setOk] = useState("");
  const a = useAccion();
  const poner = (l: CostoTratamiento[]) => { setLista(l); setCampos(Object.fromEntries(l.map((x) => [x.codigo, { lab: x.laboratorio ? dolares(x.laboratorio) : "", mat: x.materiales ? dolares(x.materiales) : "" }]))); };
  useEffect(() => { api<CostoTratamiento[]>("GET", "/api/comisiones/costos-habituales").then(poner).catch(a.setError); }, []);
  async function guardar(x: CostoTratamiento) {
    const c = campos[x.codigo] ?? { lab: "", mat: "" };
    const lab = c.lab.trim() === "" ? 0 : leerMonto(c.lab), mat = c.mat.trim() === "" ? 0 : leerMonto(c.mat);
    if (lab === null || mat === null) { a.setError(new ErrorApi(400, "Escribe los costos como 12.50")); return; }
    if (lab === x.laboratorio && mat === x.materiales) return;
    const r = await a.correr(() => api<{ laboratorio: number; materiales: number }>("PUT", `/api/comisiones/costos-habituales/${x.codigo}`, { laboratorio: lab / 100, materiales: mat / 100 }));
    if (r && lista) { setLista(lista.map((y) => (y.codigo === x.codigo ? { ...y, laboratorio: r.laboratorio, materiales: r.materiales } : y))); setOk(""); }
    else if (lista) poner(lista);
  }
  const visibles = (lista ?? []).filter((x) => !buscar.trim() || `${x.codigo} ${x.nombre}`.toLowerCase().includes(buscar.trim().toLowerCase()));
  return (
    <>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        Escribe cuánto le cuesta a la clínica el <b>laboratorio</b> y los <b>materiales</b> de cada tratamiento (por unidad). Al marcar un procedimiento como <b>realizado</b>, Donna copia ese costo al procedimiento y se descuenta antes de calcular la comisión; en la pantalla de liquidar lo puedes corregir en cada caso. Cambiar el costo habitual no modifica los procedimientos ya realizados.
      </p>
      <div className="fila" style={{ alignItems: "flex-end", flexWrap: "wrap" }}>
        <label className="campo" style={{ flex: "1 1 240px" }}>Buscar tratamiento<input value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Corona, implante, ortodoncia…" /></label>
        <button className="btn chico" disabled={a.ocupado} onClick={async () => {
          if (!confirm("¿Aplicar el costo habitual a los procedimientos ya realizados que no tienen costos y todavía no se han liquidado?")) return;
          const r = await a.correr(() => api<{ aplicados: number }>("POST", "/api/comisiones/costos-habituales/aplicar"));
          if (r) setOk(r.aplicados === 0 ? "No había procedimientos pendientes de costo." : `Se aplicó a ${r.aplicados} procedimiento${r.aplicados === 1 ? "" : "s"} ya realizado${r.aplicados === 1 ? "" : "s"}.`);
        }}>Aplicar a lo ya realizado sin costos</button>
        {ok && <span className="muted" style={{ fontSize: 13 }}>{ok}</span>}
      </div>
      <AvisoError error={a.error} />
      {!lista ? <p className="muted">Cargando…</p> : (
        <div className="tabla-scroll"><table className="tabla">
          <thead><tr><th>Código</th><th>Tratamiento</th><th>Precio</th><th>Laboratorio</th><th>Materiales</th></tr></thead>
          <tbody>{visibles.map((x) => {
            const c = campos[x.codigo] ?? { lab: "", mat: "" };
            return (
              <tr key={x.codigo} style={x.activo ? undefined : { opacity: 0.55 }}>
                <td className="mono">{x.codigo}</td><td>{x.nombre}</td><td className="mono num">{dinero(x.precio)}</td>
                <td><input className="entrada mono" style={{ width: 80 }} inputMode="decimal" placeholder="0.00" value={c.lab} onChange={(e) => setCampos({ ...campos, [x.codigo]: { ...c, lab: e.target.value } })} onBlur={() => guardar(x)} aria-label={`Laboratorio de ${x.nombre}`} /></td>
                <td><input className="entrada mono" style={{ width: 80 }} inputMode="decimal" placeholder="0.00" value={c.mat} onChange={(e) => setCampos({ ...campos, [x.codigo]: { ...c, mat: e.target.value } })} onBlur={() => guardar(x)} aria-label={`Materiales de ${x.nombre}`} /></td>
              </tr>
            );
          })}</tbody>
        </table></div>
      )}
    </>
  );
}

/* ---------------- lo del propio profesional ---------------- */

function MisComisiones() {
  const [d, setD] = useState<{ liquidaciones: Resumen[]; estimado: { total: number; procedimientos: number; sinRegla: number } } | null>(null);
  const [abierta, setAbierta] = useState<string | null>(null);
  const a = useAccion();
  useEffect(() => { api<NonNullable<typeof d>>("GET", "/api/mis-comisiones").then(setD).catch(a.setError); }, []);
  return (
    <Seccion titulo="Mis comisiones">
      <AvisoError error={a.error} />
      {d && (
        <>
          <div className="tiles">
            <div className="tile"><span>Estimado por liquidar</span><b>{dinero(d.estimado.total)}</b><small>{d.estimado.procedimientos} procedimiento{d.estimado.procedimientos === 1 ? "" : "s"} · lo cobrado hasta hoy</small></div>
          </div>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Es una estimación: se confirma cuando la administración aprueba la liquidación de la quincena. Se calcula sobre lo cobrado a tus pacientes, descontando laboratorio y materiales.</p>
          {d.liquidaciones.length === 0 && <p className="muted">Todavía no tienes liquidaciones aprobadas.</p>}
          <ul className="lista-facturas">
            {d.liquidaciones.map((x) => (
              <li key={x.id} className={abierta === x.id ? "abierto" : ""}>
                <button className="fila-factura" onClick={() => setAbierta(abierta === x.id ? null : x.id)} aria-expanded={abierta === x.id}>
                  <span className="mono">N.º {String(x.numero).padStart(5, "0")}</span>
                  <b className="cliente">{x.nombre_periodo}</b>
                  <span className={`chip ${CHIP[x.estado]}`}>{NOMBRE[x.estado]}{x.estado === "pagada" && x.pagada_en ? ` · ${fechaCorta(x.pagada_en)}` : ""}</span>
                  <b className="mono num">{dinero(x.total)}</b>
                </button>
                {abierta === x.id && <DetalleLiquidacion id={x.id} admin={false} />}
              </li>
            ))}
          </ul>
        </>
      )}
    </Seccion>
  );
}
